import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema, generateHowToSchema } from '../../utils/seo';
import DemoHero from './components/DemoHero';
import SandboxDemo from './components/SandboxDemo';
import LiveDemo from './components/LiveDemo';
import InteractiveCalculator from './components/InteractiveCalculator';
import DemoBookingForm from './components/DemoBookingForm';
import DemoFooter from './components/DemoFooter';

export default function DemoPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mtaplannerpro.com';

  useSEO({
    title: 'Interactive Demo - MtaPlannerPro | Try AI Construction Quoting Free',
    description: 'Experience MtaPlannerPro\'s AI-powered construction quoting platform. Try our interactive demo, calculate job costs instantly, and see how we transform job site photos into professional quotes with real Bunnings prices in 60 seconds.',
    keywords: 'construction quote demo, AI invoicing demo, trade quote calculator, job cost estimator, construction pricing tool, interactive demo, HVAC quote demo, plumbing invoice demo, free trial demo, tradie software demo',
    canonical: `${siteUrl}/demo`,
    ogTitle: 'Interactive Demo - Try MtaPlannerPro Free | AI Construction Quoting',
    ogDescription: 'Experience AI-powered construction quoting. Try our interactive demo and see instant results with real Bunnings prices.',
    ogUrl: `${siteUrl}/demo`,
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema({
          name: 'Interactive Demo - MtaPlannerPro',
          description: 'Experience MtaPlannerPro\'s AI-powered construction quoting platform with our interactive demo.',
          url: '/demo'
        }),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Demo', url: '/demo' }
        ]),
        generateHowToSchema({
          name: 'How to Create a Construction Quote with MtaPlannerPro',
          description: 'Step-by-step guide to creating professional construction quotes using AI-powered photo analysis',
          totalTime: 'PT1M',
          steps: [
            {
              name: 'Upload Job Photos',
              text: 'Take photos of the job site or existing work. Upload them to MtaPlannerPro\'s AI analyzer.',
              image: `${siteUrl}/demo-step-1.jpg`
            },
            {
              name: 'AI Analysis',
              text: 'Our AI identifies materials, estimates quantities, and calculates labor requirements based on Australian standards.',
              image: `${siteUrl}/demo-step-2.jpg`
            },
            {
              name: 'Review Pricing',
              text: 'Get instant pricing with real-time Bunnings and supplier costs. Adjust quantities and add custom items.',
              image: `${siteUrl}/demo-step-3.jpg`
            },
            {
              name: 'Send Quote',
              text: 'Generate a professional branded PDF quote and send it directly to your client via email or SMS.',
              image: `${siteUrl}/demo-step-4.jpg`
            }
          ]
        }),
        {
          '@type': 'VideoObject',
          name: 'MtaPlannerPro Demo - AI Construction Quoting in 60 Seconds',
          description: 'Watch how MtaPlannerPro transforms job site photos into professional quotes with real supplier pricing',
          thumbnailUrl: `${siteUrl}/demo-video-thumbnail.jpg`,
          uploadDate: '2026-02-01',
          duration: 'PT1M',
          contentUrl: 'https://www.youtube.com/watch?v=demo-video-id',
          embedUrl: 'https://www.youtube.com/embed/demo-video-id'
        }
      ]
    }
  });

  useEffect(() => {
    // Set page metadata
    document.title = 'Interactive Demo - MtaPlannerPro | Try AI Construction Invoicing';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Experience MtaPlannerPro\'s AI-powered construction invoicing platform. Try our interactive demo, calculate job costs, and see how we transform job site photos into professional invoices instantly.');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-slate-950/95 backdrop-blur-xl shadow-lg shadow-black/10 border-b border-white/5'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group cursor-pointer">
              <img 
                src="https://public.readdy.ai/ai/img_res/d1374e619b7c88bb7ec9c29306d6d801.jpg" 
                alt="MtaPlannerPro Elite Logo" 
                className="w-12 h-12 object-contain"
              />
              <span className="text-white font-bold text-xl">MtaPlannerPro</span>
            </Link>

            <div className="hidden lg:flex items-center gap-2">
              <Link
                to="/"
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-home-5-line text-base"></i> Home
                </span>
              </Link>
              <Link
                to="/invoice"
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-file-text-line text-base"></i> Quote Generator
                </span>
              </Link>
              <Link
                to="/invoice-demo"
                className="px-4 py-2 rounded-full text-sm font-medium text-slate-300 hover:text-white hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-play-circle-line text-base"></i> Invoice Demo
                </span>
              </Link>
              <a
                href="#book-demo"
                className="relative px-4 py-2 rounded-full text-sm font-medium text-white bg-white/10 cursor-pointer whitespace-nowrap"
              >
                <span className="relative z-10 flex items-center gap-2">
                  <i className="ri-calendar-check-line text-base"></i> Book Demo
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full border border-amber-500/30" />
              </a>
            </div>

            <Link
              to="/invoice"
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
            >
              <i className="ri-magic-line mr-2"></i>
              Try It Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Trusted By Strip */}
      <DemoHero />

      {/* Trusted Logos */}
      <section className="py-12 bg-slate-950 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-slate-500 text-xs uppercase tracking-wider mb-6">
            Trusted by construction teams at
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-14">
            {['Lendlease', 'Multiplex', 'Hutchinson', 'CPB', "Laing O'Rourke"].map((name, i) => (
              <span
                key={i}
                className="text-xl font-bold text-slate-600 hover:text-slate-400 transition-colors cursor-default"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ NEW: No-Signup Sandbox Demo */}
      <SandboxDemo />

      <LiveDemo />
      <InteractiveCalculator />

      {/* What You Get Section */}
      <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/15 mb-6">
              <i className="ri-gift-2-line text-amber-400 text-sm"></i>
              <span className="text-amber-400 font-medium text-sm">What You Get</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Everything in{' '}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                One Platform
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-camera-ai-line',
                title: 'Photo-to-Quote AI',
                desc: 'Upload job photos, AI identifies materials and calculates costs instantly',
              },
              {
                icon: 'ri-store-3-line',
                title: 'Bunnings Integration',
                desc: 'Real-time pricing from Bunnings and major Australian suppliers',
              },
              {
                icon: 'ri-map-pin-user-line',
                title: 'Local Market Rates',
                desc: 'Area-specific labour rates for all 8 capital cities',
              },
              {
                icon: 'ri-team-line',
                title: 'Crew Management',
                desc: 'Track shifts, GPS locations, and manage multiple job sites',
              },
              {
                icon: 'ri-box-3-line',
                title: '3D Visualization',
                desc: 'Before/after comparisons and interactive 3D project previews',
              },
              {
                icon: 'ri-file-text-line',
                title: 'Pro Invoices',
                desc: 'Branded PDF invoices with itemized breakdowns, sent in seconds',
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="group bg-white/[0.03] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-7 hover:border-amber-500/25 hover:bg-white/[0.06] transition-all duration-400 cursor-pointer"
              >
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:bg-amber-500/20 transition-all duration-300">
                  <i className={`${feature.icon} text-amber-400 text-2xl`}></i>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-amber-400 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-20 bg-slate-950 relative">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        </div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '5,000+', label: 'Active Tradies', icon: 'ri-team-line' },
              { value: '15 hrs', label: 'Saved Per Week', icon: 'ri-time-line' },
              { value: '$4.8M', label: 'Quoted Monthly', icon: 'ri-money-dollar-circle-line' },
              { value: '4.9/5', label: 'User Rating', icon: 'ri-star-fill' },
            ].map((stat, i) => (
              <div
                key={i}
                className="text-center p-6 bg-white/[0.03] border border-white/[0.06] rounded-2xl hover:border-amber-500/20 transition-all group"
              >
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                  <i className={`${stat.icon} text-amber-400 text-xl`}></i>
                </div>
                <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <DemoBookingForm />
      <DemoFooter />
    </div>
  );
}
